import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Copy, Check, Landmark, Clock, RefreshCw } from "lucide-react";
import { formatNaira } from "@/data/venues";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { bookingsApi, walletApi } from "@/lib/api";

export const Route = createFileRoute("/booking/success")({
  validateSearch: (s: Record<string, unknown>) => ({
    venue: String(s.venue ?? ""),
    ref: String(s.ref ?? ""),
    total: Number(s.total ?? 0),
    bookingId: String(s.bookingId ?? ""),
    paymentStatus: String(s.paymentStatus ?? "pending"),
    bankName: String(s.bankName ?? ""),
    accountName: String(s.accountName ?? ""),
    accountNumber: String(s.accountNumber ?? ""),
    paymentDeadline: String(s.paymentDeadline ?? ""),
  }),
  component: SuccessPage,
});

function SuccessPage() {
  const { ref, total, bookingId, paymentStatus, bankName, accountName, accountNumber, paymentDeadline } = Route.useSearch();
  const [venueName, setVenueName] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [liveStatus, setLiveStatus] = useState(paymentStatus);
  const [checking, setChecking] = useState(false);
  const [apiError, setApiError] = useState(false);
  const isPending = liveStatus === "pending";

  // Fallback bank details: if not passed via search params, fetch from wallet
  const [resolvedBankName, setResolvedBankName] = useState(bankName);
  const [resolvedAccountName, setResolvedAccountName] = useState(accountName);
  const [resolvedAccountNumber, setResolvedAccountNumber] = useState(accountNumber);

  useEffect(() => {
    if (bankName && accountName && accountNumber) return;
    if (!isPending) return;
    walletApi.getWallet()
      .then(w => {
        if (w.dedicatedBank) {
          setResolvedBankName(w.dedicatedBank.bankName);
          setResolvedAccountName(w.dedicatedBank.accountName);
          setResolvedAccountNumber(w.dedicatedBank.accountNumber);
        }
      })
      .catch(() => {});
  }, [bankName, accountName, accountNumber, isPending]);

  const [remainingMs, setRemainingMs] = useState(() => {
    if (!paymentDeadline) return 0;
    return Math.max(0, new Date(paymentDeadline).getTime() - Date.now());
  });

  useEffect(() => {
    if (!isPending || !paymentDeadline) return;
    const interval = setInterval(() => {
      setRemainingMs(Math.max(0, new Date(paymentDeadline).getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [isPending, paymentDeadline]);

  const remMins = Math.floor(remainingMs / 60_000);
  const remSecs = Math.floor((remainingMs % 60_000) / 1000);
  const isExpired = remainingMs <= 0;

  const checkBookingStatus = async () => {
    if (!bookingId) return;
    setChecking(true);
    try {
      const b = await bookingsApi.getById(bookingId);
      setApiError(false);
      setVenueName(b.venueName ?? null);
      if (b.paymentStatus && b.paymentStatus !== "pending") {
        setLiveStatus(b.paymentStatus);
      }
    } catch (err) {
      console.error("Failed to check booking status:", err);
      setApiError(true);
    }
    setChecking(false);
  };

  // Poll booking status so the page auto-updates when webhook confirms payment
  useEffect(() => {
    if (!bookingId || !isPending) return;
    let cancelled = false;
    let failCount = 0;

    const check = async () => {
      try {
        const b = await bookingsApi.getById(bookingId);
        if (!cancelled) {
          failCount = 0;
          setApiError(false);
          setVenueName(b.venueName ?? null);
          if (b.paymentStatus && b.paymentStatus !== "pending") {
            setLiveStatus(b.paymentStatus);
          }
        }
      } catch (err) {
        if (!cancelled) {
          failCount++;
          if (failCount >= 3) setApiError(true);
          console.error("Booking status poll failed (attempt", failCount, "):", err);
        }
      }
    };

    check(); // initial check
    let timeoutId: ReturnType<typeof setTimeout>;

    const schedule = () => {
      const delay = Math.min(5000 * Math.pow(2, Math.min(failCount, 4)), 60000);
      timeoutId = setTimeout(async () => {
        await check();
        if (!cancelled && isPending) schedule();
      }, delay);
    };

    schedule();
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [bookingId, isPending]);

  const copyAccount = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="mx-auto max-w-2xl px-4 py-20 text-center">
      <div className={`mx-auto grid size-16 place-items-center rounded-full ${isPending ? "bg-amber-100 text-amber-600" : "bg-success/15 text-success"}`}>
        {isPending ? <Clock className="size-9" /> : <CheckCircle2 className="size-9" />}
      </div>
      <h1 className="mt-6 font-display text-4xl font-bold">
        {isPending ? "Payment Pending" : "Booking Confirmed"}
      </h1>
      <p className="mt-3 text-muted-foreground">
        {isPending
          ? "Your booking has been created. Complete the bank transfer to confirm your reservation."
          : "Your reservation has been received. A receipt has been sent to your email."}
      </p>

      <div className="mt-10 rounded-2xl border border-border/60 bg-card p-6 text-left shadow-soft space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <span className="text-sm text-muted-foreground">Reference</span>
          <span className="font-mono text-sm font-semibold">{ref}</span>
        </div>
        {venueName && (
          <div className="space-y-2 py-2 text-sm">
            <Row label="Venue" value={venueName} />
          </div>
        )}

        {isPending && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-800">
                <Landmark className="size-5" />
                <h3 className="font-semibold text-sm">Transfer to your dedicated virtual account</h3>
              </div>
              {paymentDeadline && (
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                  isExpired ? "bg-rose-100 text-rose-600 border-rose-200" : remMins < 5 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-blue-100 text-blue-700 border-blue-200"
                }`}>
                  <Clock className="size-2.5" />
                  {isExpired ? "Payment window closed" : `${remMins}:${remSecs.toString().padStart(2, "0")} left`}
                </span>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-3 text-sm">
              <div className="rounded-lg bg-white px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">Bank</p>
                <p className="font-medium">{resolvedBankName}</p>
              </div>
              <div className="rounded-lg bg-white px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">Account Name</p>
                <p className="font-medium">{resolvedAccountName}</p>
              </div>
              <div className="rounded-lg bg-white px-3 py-2 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Account Number</p>
                  <p className="font-medium font-mono">{resolvedAccountNumber}</p>
                </div>
                <button onClick={() => copyAccount(resolvedAccountNumber)} className="text-amber-600 hover:text-amber-700">
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </button>
              </div>
            </div>
            {isExpired && (
              <p className="text-xs text-rose-700 font-medium">
                Your booking was cancelled because payment was not received within 2 hours. Please create a new booking.
              </p>
            )}
            {!isExpired && (
              <div className="space-y-2">
                <p className="text-xs text-amber-700">
                  Transfer {formatNaira(total)} to this account within {Math.floor(remMins / 60)}h {remMins % 60}min. Your booking will be confirmed automatically once payment is received.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={checkBookingStatus}
                    disabled={checking}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 transition disabled:opacity-50"
                  >
                    <RefreshCw className={`size-3 ${checking ? "animate-spin" : ""}`} />
                    {checking ? "Checking…" : "Refresh status"}
                  </button>
                  {apiError && (
                    <span className="text-[10px] text-rose-600 font-medium">
                      Unable to reach server — please check your connection or refresh the page.
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-4 font-display text-lg font-bold">
          <span>{isPending ? "Amount to pay" : "Total paid"}</span>
          <span>{formatNaira(total)}</span>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/dashboard"><Button size="lg" variant="outline">Back to Dashboard</Button></Link>
        <Link to="/hotels"><Button size="lg">Explore more venues</Button></Link>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
